import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { defaultCallBackValue } from "@/lib/lead-callback";
import type { LeadRejectionReason } from "@/lib/db-types";
import { DEFAULT_REVISIT_MONTHS, LEAD_REJECTION_REASONS, REVISIT_OPTIONS, revisitDate } from "@/lib/lead-rejection";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface CallBackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending?: boolean;
  onSubmit: (input: { callBackAt: string }) => void;
}

export function CallBackDialog({ open, onOpenChange, pending, onSubmit }: CallBackDialogProps) {
  const [value, setValue] = useState(defaultCallBackValue());
  useEffect(() => {
    if (open) setValue(defaultCallBackValue());
  }, [open]);
  const valid = !!value && new Date(value).getTime() > Date.now();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ring tilbake senere</DialogTitle>
          <DialogDescription>Leadet blir ditt igjen på dette tidspunktet.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) onSubmit({ callBackAt: new Date(value).toISOString() });
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="lead-callback-at">Dato og tid</Label>
            <Input id="lead-callback-at" type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!valid || pending}>
              Lagre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface NotRelevantInput {
  reason: LeadRejectionReason;
  /** YYYY-MM-DD, null = vurderes aldri på nytt. */
  revisitOn: string | null;
  note: string | null;
}

interface NotRelevantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending?: boolean;
  onSubmit: (input: NotRelevantInput) => void;
}

/** VIB-3032: årsak og revurdering, så avviste kan vurderes på nytt senere. VIB-3050: «Annet» krever fritekst. */
export function NotRelevantDialog({ open, onOpenChange, pending, onSubmit }: NotRelevantDialogProps) {
  const [reason, setReason] = useState<LeadRejectionReason | "">("");
  const [months, setMonths] = useState(DEFAULT_REVISIT_MONTHS);
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) {
      setReason("");
      setMonths(DEFAULT_REVISIT_MONTHS);
      setNote("");
    }
  }, [open]);
  const valid = reason !== "" && (reason !== "other" || note.trim() !== "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Avvist</DialogTitle>
          <DialogDescription>Leadet flyttes til Avviste og vurderes på nytt når datoen kommer.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && reason) onSubmit({ reason, revisitOn: revisitDate(months), note: note.trim() || null });
          }}
          className="space-y-4"
        >
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Årsak</legend>
            <RadioGroup value={reason} onValueChange={(v) => setReason(v as LeadRejectionReason)} className="grid gap-1.5 sm:grid-cols-2">
              {LEAD_REJECTION_REASONS.map((r) => (
                <label key={r.value} className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm has-[[data-state=checked]]:border-primary">
                  <RadioGroupItem value={r.value} aria-label={r.label} />
                  {r.label}
                </label>
              ))}
            </RadioGroup>
          </fieldset>
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">Vurder på nytt</legend>
            <RadioGroup value={String(months)} onValueChange={(v) => setMonths(Number(v))} className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
              {REVISIT_OPTIONS.map((o) => (
                <label key={o.months} className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm has-[[data-state=checked]]:border-primary">
                  <RadioGroupItem value={String(o.months)} aria-label={o.label} />
                  {o.label}
                </label>
              ))}
            </RadioGroup>
          </fieldset>
          <div className="space-y-1.5">
            <Label htmlFor="lead-not-relevant-note">{reason === "other" ? "Hva sa de? (påkrevd for Annet)" : "Hva sa de?"}</Label>
            <Textarea id="lead-not-relevant-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" variant="destructive" disabled={pending || !valid}>
              Avvist
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function todayValue(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

interface EmailPlannedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadName: string;
  pending?: boolean;
  onSubmit: (input: { sendOn: string; note: string | null }) => void;
}

/** VIB-3050: «Skal sende mail» på tavlen. Kortet blir liggende i søylen, sortert på datoen. */
export function EmailPlannedDialog({ open, onOpenChange, leadName, pending, onSubmit }: EmailPlannedDialogProps) {
  const [date, setDate] = useState(todayValue());
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) {
      setDate(todayValue());
      setNote("");
    }
  }, [open]);
  const valid = !!date && date >= todayValue();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Når skal mailen til {leadName} sendes?</DialogTitle>
          <DialogDescription>Kortet blir liggende i «Skal sende mail», sortert på datoen du velger.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // Kl. 12 lokal tid, så datoen ikke glir over midnatt når den lagres som tidspunkt.
            if (valid) onSubmit({ sendOn: new Date(`${date}T12:00`).toISOString(), note: note.trim() || null });
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="lead-email-date">Dato</Label>
            <Input id="lead-email-date" type="date" min={todayValue()} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lead-email-note">Hva skal sendes? (valgfri)</Label>
            <Textarea id="lead-email-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={!valid || pending}>
              Lagre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
